import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { Id, PagedParams, PagedResponse, getQueryKey } from '@feature/utils';

import * as api from './FacultyApi';
import { Faculty, FacultyRequest } from './FacultyTypes';

export const facultyKey = getQueryKey('faculty');

export function getFacultyPagedOptions(params: PagedParams): UseSuspenseQueryOptions<PagedResponse<Faculty>> {
  return {
    queryKey: facultyKey.paged(params),
    queryFn: () => api.getPaged(params),
  };
}

export function getFacultyOptions(facultyId: Id): UseSuspenseQueryOptions<Faculty> {
  return {
    queryKey: facultyKey.one(facultyId),
    queryFn: () => api.get(facultyId),
  };
}

export function getSuspenseFacultyOptions(): UseSuspenseQueryOptions<Faculty[]> {
  return {
    queryKey: facultyKey.list(),
    queryFn: () => api.getFaculties(),
  };
}

export function getFacultiesAsHeadOfOptions(): UseSuspenseQueryOptions<Faculty[]> {
  return {
    queryKey: facultyKey.as(['list', 'head-of']),
    queryFn: () => api.getFacultiesAsHeadOf(),
  };
}

export function getAccessibleFacultiesOptions(): UseSuspenseQueryOptions<Faculty[]> {
  return {
    queryKey: facultyKey.as(['list', 'accessible']),
    queryFn: () => api.getAccessibleFaculties(),
  };
}

export function useFacultyCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: FacultyRequest) => api.create(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: facultyKey.root(),
      }),
  });
}

export function useFacultyEdit(facultyId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: FacultyRequest) => api.edit(facultyId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: facultyKey.root(),
      }),
  });
}
